// src/components/modules/dashboardStats.js
// Pure helpers: turn raw timesheet entries into the numbers the dashboard shows.
// No React, no API calls — easy to unit test.

export const HOURS_PER_DAY = 8;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Parse "YYYY-MM-DD..." as a LOCAL date (avoids the UTC off-by-one you get from new Date("2026-10-13")).
const parseDate = (v) => {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toBool = (v) => {
  const s = String(v ?? "").toLowerCase();
  return v === true || v === 1 || s === "1" || s === "true" || s === "billable" || s === "yes";
};

/**
 * Map the API response to { date, iso, hours, billable, project }.
 * NOTE: the field names below are guesses with fallbacks — align them with
 * whatever your timesheet entries endpoint actually returns.
 */
export function normalizeEntries(raw = []) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const date = parseDate(e.date ?? e.entry_date ?? e.Entry_date ?? e.work_date ?? e.Work_date);
      const hours = Number(e.hours ?? e.Hours ?? e.total_hours ?? e.Total_hours ?? 0);
      if (!date || !Number.isFinite(hours) || hours <= 0) return null;
      return {
        date,
        iso: toISO(date),
        hours,
        billable: toBool(e.is_billable ?? e.Is_billable ?? e.billable ?? e.Billable),
        project: e.project_name ?? e.Project_name ?? e.projectName ?? "Unassigned",
      };
    })
    .filter(Boolean);
}

const isWorkingDay = (d, holidaySet) => d.getDay() !== 0 && d.getDay() !== 6 && !holidaySet.has(toISO(d));

/**
 * @param {Array} entries   normalized entries (see normalizeEntries)
 * @param {Object} opts     { today, hoursPerDay, holidays: ["YYYY-MM-DD", ...] }
 */
export function buildDashboardStats(entries = [], { today = new Date(), hoursPerDay = HOURS_PER_DAY, holidays = [] } = {}) {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const year = t.getFullYear();
  const month = t.getMonth();
  const holidaySet = new Set(holidays);

  const hoursByDay = new Map();
  const months = MONTH_LABELS.slice(0, month + 1).map((label) => ({ label, billable: 0, nonBillable: 0 }));
  const projectMap = new Map();
  let ytdB = 0, ytdN = 0, mtdB = 0, mtdN = 0;

  for (const e of entries) {
    if (e.date.getFullYear() !== year || e.date > t) continue; // this year, up to today only
    hoursByDay.set(e.iso, (hoursByDay.get(e.iso) || 0) + e.hours);

    const m = months[e.date.getMonth()];
    if (e.billable) { m.billable += e.hours; ytdB += e.hours; }
    else { m.nonBillable += e.hours; ytdN += e.hours; }

    if (e.date.getMonth() === month) {
      if (e.billable) mtdB += e.hours; else mtdN += e.hours;
      const p = projectMap.get(e.project) || { name: e.project, hours: 0, billableHours: 0 };
      p.hours += e.hours;
      if (e.billable) p.billableHours += e.hours;
      projectMap.set(e.project, p);
    }
  }

  // Walk Jan 1 → today: count working days and missed days (today is never "missed" yet).
  let workingDaysMTD = 0;
  let missedYTD = 0;
  const missedMTD = [];
  for (let d = new Date(year, 0, 1); d <= t; d.setDate(d.getDate() + 1)) {
    if (!isWorkingDay(d, holidaySet)) continue;
    const inMonth = d.getMonth() === month;
    if (inMonth) workingDaysMTD++;
    if (d.getTime() !== t.getTime() && !hoursByDay.get(toISO(d))) {
      missedYTD++;
      if (inMonth) missedMTD.push(new Date(d));
    }
  }

  // Calendar cells, Monday-first grid.
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const missedSet = new Set(missedMTD.map(toISO));
  const cells = Array.from({ length: offset }, (_, i) => ({ key: `blank-${i}`, status: "blank" }));
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    const iso = toISO(d);
    let status = "logged";
    if (!isWorkingDay(d, holidaySet)) status = "off";
    else if (day === t.getDate()) status = "today";
    else if (d > t) status = "future";
    else if (missedSet.has(iso)) status = "missed";
    cells.push({ key: iso, iso, day, status, hours: hoursByDay.get(iso) || 0 });
  }

  // Month close = last working day of the month.
  const monthClose = new Date(year, month, daysInMonth);
  while (!isWorkingDay(monthClose, holidaySet) && monthClose.getDate() > 1) monthClose.setDate(monthClose.getDate() - 1);

  const mtdLogged = mtdB + mtdN;
  const expected = workingDaysMTD * hoursPerDay;
  const ytdTotal = ytdB + ytdN;

  return {
    mtd: {
      billable: mtdB,
      nonBillable: mtdN,
      logged: mtdLogged,
      expected,
      notLogged: Math.max(expected - mtdLogged, 0),
      workingDays: workingDaysMTD,
      billablePct: mtdLogged ? (mtdB / mtdLogged) * 100 : 0,
    },
    ytd: {
      billable: ytdB,
      nonBillable: ytdN,
      utilization: ytdTotal ? (ytdB / ytdTotal) * 100 : 0,
      missedDays: missedYTD,
    },
    missedDays: missedMTD.map((d) => ({
      iso: toISO(d),
      date: d,
      daysOverdue: Math.round((t - d) / 86400000), // round handles DST shifts
    })),
    projects: [...projectMap.values()].sort((a, b) => b.hours - a.hours),
    months,
    cells,
    monthClose,
  };
}